import classNames from 'classnames';
import React, { ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import isFunction from 'lodash/isFunction';
import { Controller } from 'react-hook-form';

import { UiDropdownProps } from 'src/components/UI/dropdown/types';
import { getInputError } from 'src/components/UI/inputs/common';
import { uiMemoFormPropsInputCompareFn } from 'src/components/UI/UIMemoFormPropsCompareFn';
import { CSS_ANIMATION_DURATION } from 'src/config';
import { BaseOptionType } from 'src/utils/commonForm';
import { useHoverErrors } from 'src/components/UI/inputs/useHoverErrors';
import { UiHoverTooltip } from 'src/components/UI/HoverTooltip';
import { inputBaseHook } from 'src/utils/hooks/inputs';
import { useRefClickOutside } from 'src/utils/hooks/useRefClickOutside';
import { MaterialErrorMessage } from 'src/components/UI/common/MaterialErrorMessage';
import { DropdownChevronIcon } from 'src/components/UI/dropdown/Chevron';
import { SafeAnyType } from 'src/utils/safeAny';

// <dropdown> has to be declared because of css
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      dropdown: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

export const EMPTY_SELECT_VALUE = null; // empty value for other option

/**
 * simple dropdown component (select)
 */
export const UiDropdown: React.FC<UiDropdownProps> = memo(
  ({
    items,
    name,
    formProps: {
      setValue,
      getValues,
      clearErrors,
      control,
      formState: { errors },
    },
    placeholder,
    required = false,
    disabled = false,
    onChange,
    optionsCaption,
    hiddenItems,
    dataTestId,
    allowNullableValue = false,
    showHoverError,
    errorLabel,
    material,
    className,
    searchEnabled,
  }: UiDropdownProps) => {
    const { t } = useTranslation();

    const inputRef = useRef<HTMLInputElement | null>(null);

    const [optionsListMargin, setOptionsListMargin] = useState(0);

    const [searchInputValue, setSearchInputValue] = useState('');

    const mountRef = useRef(false);

    useEffect(() => inputBaseHook(mountRef, name, clearErrors), [clearErrors, name]);

    const error = getInputError(errors, name);

    /** check if there are any errors */
    const errorRef = useRef(false);

    useEffect(() => {
      errorRef.current = !!error;
    }, [error]);
    /** end check if there are any errors */

    const value = getValues(name);

    const [show, setShow] = useState(false);
    const [render, setRender] = useState(false);

    const getSelectedItemLabel = useCallback(
      (fieldValue: SafeAnyType) => {
        if (!searchEnabled && !allowNullableValue && [undefined, null].includes(fieldValue)) {
          return optionsCaption;
        }
        return items.find((item) => item.value === fieldValue)?.label;
      },
      [allowNullableValue, items, optionsCaption, searchEnabled]
    );

    useEffect(() => {
      setSearchInputValue(getSelectedItemLabel(value) || '');
    }, [getSelectedItemLabel, value]);

    const ref = useRefClickOutside<HTMLDivElement>(show, () => setShow(false));

    const getNoHiddenItems = (isHidden: UiDropdownProps['hiddenItems'], dropdownItems: UiDropdownProps['items']) =>
      isHidden ? dropdownItems.filter((item) => !isHidden.includes(item.value)) : dropdownItems;

    const [options, setOptions] = useState<UiDropdownProps['items']>(() => getNoHiddenItems(hiddenItems, items));

    useEffect(() => {
      setOptions(getNoHiddenItems(hiddenItems, items));
    }, [hiddenItems, items]);

    const hasPlaceholder = !!placeholder;

    /** scroll to selected option manually */
    const scrollToSelectedOption = useCallback(() => {
      const selectedIndex = options.findIndex((item) => item.value === value);
      const ul = ref.current?.querySelector('ul');

      if (ul && selectedIndex !== -1) {
        const selectedElement: HTMLLIElement | null = ul.querySelector(`li:nth-of-type(${selectedIndex + 1})`);

        if (selectedElement) {
          const paddingTopOption = material && !hasPlaceholder ? 14 : 0;
          ul.scrollTop = selectedElement.offsetTop - paddingTopOption;
        }
      }
    }, [hasPlaceholder, material, options, ref, value]);

    const rules = useMemo(
      () => ({
        validate: (v: unknown) => {
          if (mountRef.current) {
            if (required && !v) {
              return t('common.validation.required', { label: errorLabel || 'This field' }).toString();
            }
          }
          return undefined;
        },
      }),
      [errorLabel, required, t]
    );

    /** track a click on outer elements when dropdown is opened */
    const hasEventListener = useRef(false);

    useEffect(() => {
      if (show) {
        hasEventListener.current = true;
      } else if (hasEventListener) {
        hasEventListener.current = false;
        setTimeout(() => {
          setRender(false);
        }, CSS_ANIMATION_DURATION / 2);
      }
    }, [show]);
    /** end track a click on outer elements... */

    const { hoverMessage, handleChangeInputHover } = useHoverErrors({
      name,
      clearErrors,
      error,
      showAnyError: showHoverError,
    });

    const onClick = useCallback(() => {
      if (!disabled) {
        if (searchEnabled && inputRef.current) {
          inputRef.current.focus();

          const INPUT_MARGIN = 5;
          setOptionsListMargin(inputRef.current.offsetHeight + INPUT_MARGIN);
        }

        setRender(true);

        /** render just a little later so the animation will be correct */
        setTimeout(() => {
          scrollToSelectedOption();

          setShow(true);
        }, 0);
      }
    }, [disabled, scrollToSelectedOption, searchEnabled]);

    const onSelect = useCallback(
      (value: BaseOptionType<typeof EMPTY_SELECT_VALUE> | undefined) => () => {
        setShow(false);
        handleChangeInputHover();

        if (value === undefined) {
          setValue(name, undefined);
          return;
        }

        /** optimization of setting a value */
        if (value.value !== getValues(name)) {
          if (required && errorRef.current) {
            setValue(name, value.value, { shouldValidate: true });
          } else {
            setValue(name, value.value); // the problem of rerender
          }

          if (isFunction(onChange)) {
            onChange(value);
          }
        }
      },
      [handleChangeInputHover, getValues, name, onChange, setValue, required]
    );

    const filterOptions = useCallback(
      (text: string) => {
        const actualItems = getNoHiddenItems(hiddenItems, items);

        if (!text) {
          setOptions(actualItems);
        } else {
          setOptions(actualItems.filter((item) => item.label.toLowerCase().includes(text.toLowerCase())));
        }
      },
      [hiddenItems, items]
    );

    const handleChangeSearchInput = (event: ChangeEvent<HTMLInputElement>) => {
      setSearchInputValue(event.target.value);
      filterOptions(event.target.value);
    };

    const selectedLabel = useMemo(() => getSelectedItemLabel(value) || '', [getSelectedItemLabel, value]);

    useEffect(() => {
      /** reset the search input value to the last selected, when the list of options are closed */
      if (searchEnabled && !render && inputRef.current && inputRef.current.value !== selectedLabel) {
        setSearchInputValue(selectedLabel);
        filterOptions(selectedLabel);
      }
    }, [filterOptions, searchEnabled, render, selectedLabel]);

    if (material) {
      return (
        <div className={classNames('dropdown-material custom', className)}>
          <dropdown className="custom-dropdown">
            <div className="aex-dropdown-wrapper" ref={ref}>
              {render && (
                <div className="aex-dropdown-list-wrapper">
                  {placeholder && !searchEnabled && (
                    <div className="dropdown-list-placeholder">
                      <span role="presentation" onClick={() => setShow(false)}>
                        {placeholder}
                      </span>
                      <div role="presentation" className="aex-dropdown-caret" onClick={() => setShow(false)}>
                        <DropdownChevronIcon />
                      </div>
                    </div>
                  )}
                  <ul
                    className={classNames('aex-dropdown-list', {
                      anim: show,
                      withPlaceholder: hasPlaceholder && !searchEnabled,
                    })}
                    style={searchEnabled ? { marginTop: optionsListMargin } : undefined}
                  >
                    {optionsCaption && (
                      <li
                        role="presentation"
                        className={classNames('aex-dropdown-option caption', {
                          selected: [undefined, null].includes(value),
                        })}
                        onClick={onSelect(undefined)}
                      >
                        {optionsCaption}
                      </li>
                    )}
                    {options.map((item) => (
                      <li
                        key={item.value}
                        role="presentation"
                        className={classNames('aex-dropdown-option', {
                          selected: item.value === value,
                        })}
                        onClick={onSelect(item)}
                      >
                        {item.label}
                      </li>
                    ))}

                    {searchEnabled && !!searchInputValue && !options.length && (
                      <li key="noOptions" className="aex-dropdown-option no-options">
                        {t('common.ui.noOptions')}
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {searchEnabled ? (
                <Controller
                  control={control}
                  name={name}
                  rules={rules}
                  render={({ field }) => (
                    <input
                      type="text"
                      className={classNames('aex-dropdown-input-search', {
                        required: !!error && required,
                        error: !!error,
                        disabled,
                        noFocus: render,
                      })}
                      onClick={onClick}
                      value={searchInputValue}
                      onChange={handleChangeSearchInput}
                      placeholder={getSelectedItemLabel(field.value) ?? placeholder}
                      data-testid={dataTestId}
                      disabled={disabled}
                      ref={inputRef}
                    />
                  )}
                />
              ) : (
                <Controller
                  control={control}
                  name={name}
                  rules={rules}
                  render={({ field }) => (
                    <input
                      type="text"
                      className={classNames('aex-dropdown-input', {
                        required: !!error && required,
                        error: !!error,
                        disabled,
                        noFocus: render,
                      })}
                      readOnly
                      onClick={onClick}
                      value={getSelectedItemLabel(field.value)}
                      placeholder={placeholder}
                      data-testid={dataTestId}
                      disabled={disabled}
                    />
                  )}
                />
              )}

              <div
                role="presentation"
                className={classNames('aex-dropdown-caret', {
                  disabled,
                })}
                onClick={onClick}
              >
                <DropdownChevronIcon />
              </div>
            </div>
          </dropdown>
          <MaterialErrorMessage error={hoverMessage} />
        </div>
      );
    }

    return (
      <dropdown className="custom-dropdown">
        <UiHoverTooltip id={name} text={hoverMessage} render={!!hoverMessage && !render} noWrapper>
          <div className="aex-dropdown-wrapper" ref={ref}>
            {render && (
              <div className="aex-dropdown-list-wrapper">
                <ul
                  className={classNames('aex-dropdown-list', {
                    anim: show,
                  })}
                >
                  {optionsCaption && (
                    <li
                      role="presentation"
                      className={classNames('aex-dropdown-option caption', {
                        selected: [undefined, null].includes(value),
                      })}
                      onClick={onSelect(undefined)}
                    >
                      {optionsCaption}
                    </li>
                  )}
                  {options.map((item) => (
                    <li
                      key={item.value}
                      role="presentation"
                      className={classNames('aex-dropdown-option', {
                        selected: item.value === value,
                      })}
                      onClick={onSelect(item)}
                    >
                      {item.label}
                    </li>
                  ))}

                  {searchEnabled && !!searchInputValue && !options.length && (
                    <li key="noOptions" className="aex-dropdown-option no-options">
                      {t('common.ui.noOptions')}
                    </li>
                  )}
                </ul>
              </div>
            )}

            {searchEnabled ? (
              <input
                type="text"
                className={classNames('aex-dropdown-input-search', {
                  required: !!error && required,
                  error: !!error,
                  disabled,
                })}
                onClick={onClick}
                value={searchInputValue}
                onChange={handleChangeSearchInput}
                placeholder={placeholder}
                data-testid={dataTestId}
                ref={inputRef}
              />
            ) : (
              <Controller
                control={control}
                name={name}
                rules={rules}
                render={({ field }) => (
                  <input
                    type="text"
                    className={classNames('aex-dropdown-input', {
                      required: !!error && required,
                      error: !!error,
                      disabled,
                    })}
                    readOnly
                    onClick={onClick}
                    value={getSelectedItemLabel(field.value)}
                    placeholder={placeholder}
                    data-testid={dataTestId}
                  />
                )}
              />
            )}

            <div
              role="presentation"
              className={classNames('aex-dropdown-caret material-icons', {
                disabled,
              })}
              onClick={onClick}
            >
              expand_more
            </div>
          </div>
        </UiHoverTooltip>
      </dropdown>
    );
  },
  uiMemoFormPropsInputCompareFn
);
